import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { EditIcon, DeleteIcon, CloseIcon } from "../../../../assets";
import AddItemModal from "../../../../components/modals/addItemModal/AddItemModal";
import ItemPresetModal from "../../../../components/modals/itemPresetModal/ItemPresetModal";
import SecondaryButton from "../../../../components/secondaryButton/SecondaryButton";
import AreYouSureModal from "../../../../components/modals/areYouSureModal/AreYouSureModal";
import ConfigureVendingControllerModal from "../../../../components/modals/configureVendingController/ConfigureVendingControllerModal";
import UpdateVendingControllerModal from "../../../../components/modals/updateVendingControllerModal/UpdateVendingControllerModal";
import ResourceList from "../../../../components/resourceList/ResourceList";
import { getAllTaxLabels } from "../../../../store/actions/admin/tax";
import {
  addItemToVendingController,
  addItemToVendingControllerReset,
  addVendingControllerToFiscalDevice,
  addVendingControllerToFiscalDeviceResetState,
  deleteItem,
  deleteItemResetState,
  getVendingController,
  getVendingControllerInvoices,
  updateItem,
  updateItemResetState,
  updateVendingControllerResetInfo,
} from "../../../../store/actions/admin/vendingControllers";
import styles from "./SingleVendingController.module.scss";
import moment from "moment-timezone";
import { useHistory } from "react-router-dom";
import { isMobile } from "react-device-detect";
import SyncToIntervendModal from "../../../../components/modals/syncToIntervendModal/SyncToIntervendModal";
import ChooseVendingMachineModal from "../../../../components/modals/chooseVendingMachineModal/ChooseVendingMachineModal";
import vendingControllersService from "../../../../apis/services/admin/vendingControllersService";
import config from "../../../../config";

// True for the operator's own test taxpayers (Simons d.o.o.) — never offer to sync those to Intervend.
const isExcludedPib = (pib) =>
  !!pib && (config.intervendSync?.excludedPibs || []).includes(String(pib).trim());

const HEADER_VALUES = [
  { label: "BROJ RAČUNA", key: "invoiceNumber", sortable: true },
  { label: "VREME NA PFR SERVERU", key: "sdcDateTime", sortable: true },
  { label: "UKUPAN IZNOS", key: "totalAmount", sortable: true },
  { label: "UKUPAN IZNOS POREZA", key: "taxTotalAmount", sortable: false },
  { label: "RAČUN" },
  // { label: "MAK ADRESA", key: "macAddress", sortable: true },
  // { label: "ONLINE", key: "lastOnlineAt", sortable: true },
];

const ITEM_HEADER_VALUES = [
  { label: "NAZIV", key: "name" },
  { label: "GTIN", key: "gtin" },
  { label: "JEDINICNA CENA", key: "unitPrice" },
  { label: "PORESKE LABELE", key: "taxLabels" },
  { label: "IDENTIFIKATOR", key: "identifier" },
  { label: "", key: "actions" },
];

const SingleVendingController = () => {
  const { id } = useParams();
  const dispatch = useDispatch();

  const [configModalOpen, setConfigModalOpen] = useState(false);
  const [addItemModalOpen, setAddItemModalOpen] = useState(false);
  const [presetModalOpen, setPresetModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [updateVendingControllerModalOpen, setUpdateVendingControllerOpen] =
    useState(false);
  const [itemForDelete, setItemForDelete] = useState(null);
  const [itemForEdit, setItemForEdit] = useState(null);

  // Intervend sync prompt: after a successful attach/item change we offer to push it to Intervend.
  // syncPrompt = null | { kind: 'attach' | 'item-upsert' | 'item-delete', identifier? }
  const [syncPrompt, setSyncPrompt] = useState(null);
  const [syncing, setSyncing] = useState(false);
  // Refs so the async success effects read the last-touched item identifier without stale closures.
  const lastItemIdentifierRef = useRef(null);
  const lastDeletedIdentifierRef = useRef(null);
  // Company PIB captured when the edit modal opens (BEFORE the save overwrites data), so the attach
  // prompt is gated on the PRE-edit company: a real→Simons move is a "detach" and must still prompt.
  const preEditCompanyPibRef = useRef(null);

  const onConfirmDelete = () => {
    lastDeletedIdentifierRef.current = itemForDelete?.identifier || null;
    dispatch(deleteItem({ id: data?.id, itemId: itemForDelete.id }));
  };

  const history = useHistory();

  const invoices = useSelector(
    (state) => state.vendingControllers.vendingControllerInvoices
  );

  const vendingController = useSelector(
    (state) => state.vendingControllers.vendingController
  );
  const { data } = vendingController || [];

  const companyPib = data?.fiscalDevice?.businessUnit?.company?.PIB;

  // Offer to push the just-completed change to Intervend, unless this is an excluded test taxpayer.
  // For "attach" the prompt is skipped only when BOTH sides of the move are excluded: real→Simons
  // must prompt (IV detach), and Simons/none→real must prompt too (IV attach + VM choice). The
  // store refreshes on save, so companyPib here is already the POST-edit company.
  // Item changes check the current company (don't sync items on a Simons device).
  // Attach always prompts — including while the LPFR is still on the Simons prep taxpayer — so an
  // installation can be prepared and mirrored to Intervend before the customer's smart card moves the
  // LPFR to their company. Item-change syncs still skip a Simons device.
  const maybePromptSync = (kind, identifier) => {
    if (kind !== "attach" && isExcludedPib(companyPib)) {
      return;
    }
    setSyncPrompt({ kind, identifier });
  };

  // vendingMachine (attach only): { action: 'attach', vendingMachineId } | { action: 'create', name } | null
  const runSync = async (vendingMachine = null) => {
    if (!syncPrompt || syncing) return;
    setSyncing(true);
    try {
      let res;
      if (syncPrompt.kind === "attach") {
        res = await vendingControllersService.syncVcAttachToIntervend({ id: data?.id, vendingMachine });
      } else if (syncPrompt.kind === "item-upsert") {
        res = await vendingControllersService.syncItemUpsertToIntervend({ id: data?.id, identifier: syncPrompt.identifier });
      } else if (syncPrompt.kind === "items-all") {
        res = await vendingControllersService.syncAllItemsToIntervend({ id: data?.id });
      } else {
        res = await vendingControllersService.syncItemDeleteToIntervend({ id: data?.id, identifier: syncPrompt.identifier });
      }
      const queued = res?.data?.queued;
      toast.info(
        queued
          ? syncPrompt.kind === "items-all"
            ? res?.message || "Stavke poslate u Intervend"
            : "Poslato u Intervend"
          : res?.message || "Sinhronizacija preskočena"
      );
    } catch (e) {
      toast.error(e?.message || "Sinhronizacija sa Intervend-om nije uspela");
    } finally {
      setSyncing(false);
      setSyncPrompt(null);
    }
  };

  const [detachConfirmOpen, setDetachConfirmOpen] = useState(false);
  const [detaching, setDetaching] = useState(false);

  // Detach the controller from its fiscal device: it moves to the pending list on SDF, and is
  // detached on Intervend too (the attach trigger detects the missing fiscal device → detach).
  const runDetach = async () => {
    if (detaching || !data?.id) return;
    setDetaching(true);
    try {
      await vendingControllersService.detachVendingController({ id: data.id });
      try {
        await vendingControllersService.syncVcAttachToIntervend({ id: data.id, vendingMachine: null });
      } catch {
        /* best-effort IV detach; SDF detach already succeeded */
      }
      toast.info("Kontroler otkačen — prešao u pending");
      dispatch(getVendingController({ id }));
    } catch (e) {
      toast.error(e?.message || "Otkačivanje nije uspelo");
    } finally {
      setDetaching(false);
      setDetachConfirmOpen(false);
    }
  };

  const vendingToFiscalDevice = useSelector(
    (state) => state.vendingControllers.addVendingControllerToFiscalDevice
  );

  const addItemToVendingControllerInfo = useSelector(
    (state) => state.vendingControllers.addItemToVendingControllerInfo
  );

  const deleteItemInfo = useSelector(
    (state) => state.vendingControllers.deleteItemInfo
  );

  const updateItemInfo = useSelector(
    (state) => state.vendingControllers.updateItemInfo
  );

  const updateVendingControllerInfo = useSelector(
    (state) => state.vendingControllers.updateVendingControllerInfo
  );

  const [searchTerm, setSearchTerm] = useState("");

  const onLoadPage = (page) => {
    dispatch(
      getVendingControllerInvoices({
        id,
        page,
        perPage: invoices.perPage,
        term: searchTerm,
        sortBy: "",
        order: "",
      })
    );
  };

  const onSort = (sortBy, order) => {
    dispatch(
      getVendingControllerInvoices({
        id: id,
        page: invoices.page,
        perPage: invoices.perPage,
        term: searchTerm,
        sortBy,
        order,
      })
    );
  };

  useEffect(() => {
    dispatch(
      getVendingControllerInvoices({ id, page: 1, perPage: 10, term: "" })
    );
  }, [id, dispatch]);

  useEffect(() => {
    if (updateVendingControllerInfo.success) {
      toast.info("Sacuvano");
      setUpdateVendingControllerOpen(false);
      dispatch(updateVendingControllerResetInfo());
      maybePromptSync("attach");
    }
  }, [updateVendingControllerInfo.success]);

  useEffect(() => {
    if (updateVendingControllerInfo.error) {
      toast.warn("Greska");
      dispatch(updateVendingControllerResetInfo());
    }
  }, [updateVendingControllerInfo.error]);

  useEffect(() => {
    if (deleteItemInfo.error) {
      toast.error(deleteItemInfo.error);
      dispatch(deleteItemResetState());
    }
  }, [deleteItemInfo.error, dispatch]);

  useEffect(() => {
    if (deleteItemInfo.success) {
      toast.info("Stavka obrisana");
      dispatch(deleteItemResetState());
      setConfirmModalOpen(false);
      maybePromptSync("item-delete", lastDeletedIdentifierRef.current);
    }
  }, [deleteItemInfo.success, dispatch]);

  useEffect(() => {
    if (updateItemInfo.success) {
      toast.info("Stavka izmenjena");
      dispatch(updateItemResetState());
      setAddItemModalOpen(false);
      setItemForEdit(null);
      maybePromptSync("item-upsert", lastItemIdentifierRef.current);
    }
  }, [updateItemInfo.success]);

  const handleAddItem = ({ item }) => {
    lastItemIdentifierRef.current = item?.identifier || null;
    dispatch(addItemToVendingController({ id: data?.id, item }));
  };

  const handleUpdateItem = ({ item }) => {
    lastItemIdentifierRef.current = item?.identifier || null;
    dispatch(updateItem({ id: data?.id, item }));
  };

  useEffect(() => {
    if (id) dispatch(getVendingController({ id }));
  }, [id, dispatch]);

  const onChoose = (fiscalDeviceId, id) => {
    dispatch(addVendingControllerToFiscalDevice({ id, fiscalDeviceId }));
  };

  useEffect(() => {
    dispatch(getAllTaxLabels());
  }, [dispatch]);

  useEffect(() => {
    if (addItemToVendingControllerInfo.error) {
      toast.error(addItemToVendingControllerInfo.error);
      dispatch(addItemToVendingControllerReset());
    }
  }, [addItemToVendingControllerInfo.error, dispatch]);

  useEffect(() => {
    if (addItemToVendingControllerInfo.success) {
      toast.info("Stavka dodata");
      dispatch(addItemToVendingControllerReset());
      setAddItemModalOpen(false);
      maybePromptSync("item-upsert", lastItemIdentifierRef.current);
    }
  }, [addItemToVendingControllerInfo.success, dispatch]);

  useEffect(() => {
    if (vendingToFiscalDevice.success) {
      toast.info("Fiskalni uređaj dodat");
      setConfigModalOpen(false);
      dispatch(addVendingControllerToFiscalDeviceResetState());
      maybePromptSync("attach");
    }
  }, [vendingToFiscalDevice.success, dispatch]);

  useEffect(() => {
    if (vendingToFiscalDevice.error) {
      toast.error(vendingToFiscalDevice.error);
    }
  }, [vendingToFiscalDevice.error]);

  const renderAdditionalInfo = () => {
    const lastTimeOnline = data?.lastOnlineAt
      ? moment(data?.lastOnlineAt)
        .tz(moment.tz.guess())
        .format("DD-MM-YYYY HH:mm:ss")
      : "";

    const networkInfo = data?.config?.network;
    const otaUrl = data?.config?.otaUrl;
    const otaVersion = data?.config?.otaVersion;

    const isOnline =
      new Date().getTime() - vendingController.lastOnlineAt > 120 * 1000
        ? false
        : true;
    if (data?.fiscalDevice) {
      return (
        <>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Ime:</p>
            </div>
            <div className={classNames(styles.configValue, styles.touchable)}>
              <p>{data?.name}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Serijski broj:</p>
            </div>
            <div className={classNames(styles.configValue, styles.touchable)}>
              <p>{data?.serialNumber}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Mac adresa:</p>
            </div>
            <div className={styles.configValue}>
              <p>{data?.macAddress}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Firma:</p>
            </div>
            <div className={styles.configValue}>
              <p>{data.fiscalDevice.businessUnit.company.name}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Poslovni prostor:</p>
            </div>
            <div className={styles.configValue}>
              <p>{data.fiscalDevice.businessUnit.name}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Fiskalni uređaj:</p>
            </div>

            <div className={styles.configValue}>
              <Link
                className={styles.link}
                to={"/fiscal-devices/" + data?.fiscalDevice.id}
              >
                <p className={styles.clickable}>{data.fiscalDevice.name}</p>
              </Link>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Zanji put online:</p>
            </div>
            <div className={styles.configValue}>
              <div
                className={classNames(isOnline ? styles.online : styles.ofline)}
              />
              <p>{lastTimeOnline}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Verzija:</p>
            </div>
            <div className={styles.configValue}>
              <p>{data?.version}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Status E:</p>
            </div>
            <div className={styles.configValue}>
              <p>{data?.status1}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Status I:</p>
            </div>
            <div className={styles.configValue}>
              <p>{data?.status2}</p>
            </div>
          </div>

          {/* Network information if exists */}
          {networkInfo && (
            <>
              <div className={styles.configRow}>
                <div className={styles.configLabel}>
                  <p className={styles.textBold}>Network IP:</p>
                </div>
                <div className={styles.configValue}>
                  <p>{networkInfo.ipAddress}</p>
                </div>
              </div>
              <div className={styles.configRow}>
                <div className={styles.configLabel}>
                  <p className={styles.textBold}>SSID:</p>
                </div>
                <div className={styles.configValue}>
                  <p>{networkInfo.ssid}</p>
                </div>
              </div>
              <div className={styles.configRow}>
                <div className={styles.configLabel}>
                  <p className={styles.textBold}>Password:</p>
                </div>
                <div className={styles.configValue}>
                  <p>{networkInfo.pwd}</p>
                </div>
              </div>
              <div className={styles.configRow}>
                <div className={styles.configLabel}>
                  <p className={styles.textBold}>RSSI:</p>
                </div>
                <div className={styles.configValue}>
                  <p>{networkInfo.rssi}</p>
                </div>
              </div>
            </>
          )}

          {/* OTA information if exists */}
          {otaUrl && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>OTA URL:</p>
              </div>
              <div className={styles.configValue}>
                <p>{otaUrl}</p>
              </div>
            </div>
          )}

          {otaVersion && (
            <div className={styles.configRow}>
              <div className={styles.configLabel}>
                <p className={styles.textBold}>OTA Version:</p>
              </div>
              <div className={styles.configValue}>
                <p>{otaVersion}</p>
              </div>
            </div>
          )}
        </>
      );
    } else {
      return (
        <>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Mac adresa:</p>
            </div>
            <div className={styles.configValue}>
              <p>{data?.macAddress}</p>
            </div>
          </div>
          <div className={styles.configRow}>
            <div className={styles.configLabel}>
              <p className={styles.textBold}>Serijski broj:</p>
            </div>
            <div className={classNames(styles.configValue, styles.touchable)}>
              <p>{data?.serialNumber}</p>
            </div>
          </div>
        </>
      );
    }
  };

  const renderResourceListRow = (invoice, index) => {
    let totalTaxAmount = 0;
    invoice?.taxItems.forEach((taxItem) => {
      totalTaxAmount += taxItem.amount;
    });

    const localDt = invoice.sdcDateTimeTimestamp
      ? moment(invoice.sdcDateTimeTimestamp)
        .tz(moment.tz.guess())
        .format("HH:mm:ss DD-MM-YYYY")
      : moment
        .utc(invoice.createdAt)
        .tz(moment.tz.guess())
        .format("HH:mm:ss DD-MM-YYYY");

    if (isMobile) {
      return (
        <div className={styles.listCard}>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Broj računa</p>
            <p className={styles.rowCardValue}>{invoice.invoiceNumber}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Vreme na PFR serveru</p>
            <p className={styles.rowCardValue}>{localDt}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Ukupan iznos</p>
            <p className={styles.rowCardValue}>{invoice.totalAmount}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Porez</p>
            <p className={styles.rowCardValue}>{totalTaxAmount}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Zurnal</p>
            <a
              href={invoice.convertedUrlPreview}
              target="_blank"
              className={styles.journalLink}
            >
              Prikaži zurnal
            </a>
          </div>
        </div>
      );
    } else
      return (
        <div
          className={classNames(
            styles.row,
            index % 2 === 0 ? styles.even : styles.odd
          )}
          key={invoice.id}
          onClick={() => { }}
        >
          <div className={styles.cell}>
            <p>{invoice.invoiceNumber}</p>
          </div>
          <div className={styles.cell}>
            <p>{localDt}</p>
          </div>
          <div className={styles.cell}>
            <p>{invoice.totalAmount}</p>
          </div>
          <div className={styles.cell}>
            <p>{totalTaxAmount}</p>
          </div>
          <div className={styles.cell}>
            <a
              href={invoice.convertedUrlPreview}
              target="_blank"
              className={styles.journalLink}
            >
              Prikaži zurnal
            </a>
          </div>
        </div>
      );
  };

  const renderItemsListRow = (item, index) => {
    if (isMobile) {
      return (
        <div className={styles.listCard}>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Ime</p>
            <p className={styles.rowCardValue}>{item.name}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>GTIN</p>
            <p className={styles.rowCardValue}>{item.gtin}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Jedinicna cena</p>
            <p className={styles.rowCardValue}>{item.unitPrice}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Poreske labele</p>
            <p className={styles.rowCardValue}>{JSON.stringify(item.labels)}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Identifikator</p>
            <p className={styles.rowCardValue}>{item.identifier}</p>
          </div>
          <div className={styles.rowCard}>
            <p className={styles.rowCardTitle}>Akcije</p>
            <div className={styles.actionsWrap}>
              <img
                src={DeleteIcon}
                alt="delete"
                className={styles.deleteIcon}
                onClick={() => {
                  setItemForDelete(item);
                  setConfirmModalOpen(true);
                }}
              />
              <img
                src={EditIcon}
                alt="edit"
                className={styles.editButton}
                onClick={() => {
                  setItemForEdit(item);
                  setAddItemModalOpen(true);
                }}
              />
            </div>
          </div>
        </div>
      );
    } else
      return (
        <div
          className={classNames(
            styles.row,
            index % 2 === 0 ? styles.even : styles.odd
          )}
          key={item.id}
        >
          <div className={styles.cell}>
            <p>{item.name}</p>
          </div>
          <div className={styles.cell}>
            <p>{item.gtin}</p>
          </div>
          <div className={styles.cell}>
            <p>{item.unitPrice}</p>
          </div>
          <div className={styles.cell}>
            <p>{JSON.stringify(item.labels)}</p>
          </div>
          <div className={styles.cell}>
            <p>{item.identifier}</p>
          </div>
          <div className={styles.cell}>
            <div className={styles.actionsWrap}>
              <img
                src={DeleteIcon}
                alt="delete"
                className={styles.deleteIcon}
                onClick={() => {
                  setItemForDelete(item);
                  setConfirmModalOpen(true);
                }}
              />
              <img
                src={EditIcon}
                alt="edit"
                className={styles.editButton}
                onClick={() => {
                  setItemForEdit(item);
                  setAddItemModalOpen(true);
                }}
              />
            </div>
          </div>
        </div>
      );
  };

  return (
    <div className={styles.content}>
      <AreYouSureModal
        modalOpen={confirmModalOpen}
        closeModal={() => {
          setConfirmModalOpen(false);
        }}
        onConfirm={onConfirmDelete}
      />
      <AreYouSureModal
        modalOpen={detachConfirmOpen}
        closeModal={() => setDetachConfirmOpen(false)}
        onConfirm={runDetach}
      />
      {/* Item changes keep the plain confirm; VC↔FD attach goes through the VM-choice step
          (link to an existing IV machine without a controller, or create a new one). */}
      <SyncToIntervendModal
        modalOpen={!!syncPrompt && syncPrompt.kind !== "attach"}
        closeModal={() => setSyncPrompt(null)}
        onConfirm={() => runSync()}
        loading={syncing}
        title={
          syncPrompt?.kind === "items-all"
            ? "Sinhronizovati sve stavke sa Intervend-om?"
            : syncPrompt?.kind === "item-delete"
            ? "Sinhronizovati brisanje stavke sa Intervend-om?"
            : "Sinhronizovati stavku sa Intervend-om?"
        }
        description={
          syncPrompt?.kind === "items-all"
            ? "Svi proizvodi sa ovog kontrolera biće poslati na Intervend (postojeći se ažuriraju). Fiskalni zapis je već sačuvan; ovo je opcionalno."
            : "Izmena stavke biće preneta na Intervend (usklađivanje kataloga). Fiskalni zapis je već sačuvan; ovo je opcionalno."
        }
      />
      <ChooseVendingMachineModal
        modalOpen={!!syncPrompt && syncPrompt.kind === "attach"}
        closeModal={() => setSyncPrompt(null)}
        vendingControllerId={data?.id}
        onConfirm={(vendingMachine) => runSync(vendingMachine)}
        loading={syncing}
      />
      <ConfigureVendingControllerModal
        modalOpen={configModalOpen}
        onClose={() => setConfigModalOpen(false)}
        onChoose={onChoose}
        vendingControllerId={data?.id}
        attempt={vendingToFiscalDevice?.attempt}
        vendingController={data}
      />
      <AddItemModal
        modalOpen={addItemModalOpen}
        onClose={() => {
          setAddItemModalOpen(false);
          setItemForEdit(null);
        }}
        itemForEdit={itemForEdit}
        handleAddItem={handleAddItem}
        handleUpdateItem={handleUpdateItem}
      />
      <UpdateVendingControllerModal
        modalOpen={updateVendingControllerModalOpen}
        onClose={() => setUpdateVendingControllerOpen(false)}
        vendignController={vendingController?.data}
      />
      <ItemPresetModal
        modalOpen={presetModalOpen}
        onClose={() => setPresetModalOpen(false)}
        vendingControllerId={data?.id}
        existingItems={data?.items || []}
        onSuccess={() => {
          setPresetModalOpen(false);
          dispatch(getVendingController({ id }));
          maybePromptSync("items-all");
        }}
      />
      <div className={styles.titleWrap}>
        <img
          src={CloseIcon}
          alt="close"
          onClick={() => {
            history.goBack();
          }}
          className={styles.closeIcon}
        />
        <p className={isMobile ? styles.titleMobile : styles.title}>
          Vending kontroler
        </p>
      </div>
      <div className={classNames(styles.gridHalves, styles.marginBottom)}>
        <div className={classNames(styles.sectionMax)}>
          <div className={styles.sectionHeader}>
            <p className={styles.sectionTitle}>Informacije o kontroleru</p>
            <div className={styles.actionsWrap}>
              {data?.fiscalDevice && (
                <SecondaryButton
                  name="Otkači"
                  onClick={() => setDetachConfirmOpen(true)}
                />
              )}
              <img
                src={EditIcon}
                alt="edit"
                className={styles.editButton}
                onClick={() => {
                  // setConfigModalOpen(true);
                  preEditCompanyPibRef.current = companyPib;
                  setUpdateVendingControllerOpen(true);
                }}
              />
            </div>
          </div>
          <div className={classNames(styles.card)}>
            <div className={styles.cardContent}>{renderAdditionalInfo()}</div>
          </div>
        </div>
      </div>
      <div
        className={classNames(
          styles.section,
          styles.marginTop,
          styles.itemsWrap
        )}
        p
      >
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>Stavke</p>
          <div className={styles.actionsWrap}>
            <SecondaryButton
              name="Primeni šablon"
              onClick={() => setPresetModalOpen(true)}
            />
            <img
              src={EditIcon}
              alt="edit"
              className={styles.editButton}
              onClick={() => setAddItemModalOpen(true)}
            />
          </div>
        </div>
        {data?.items && (
          <div>
            <ResourceList
              rowRender={renderItemsListRow}
              onSort={() => { }}
              onLoadPage={() => { }}
              headerValues={ITEM_HEADER_VALUES}
              resource={{ data: data?.items }}
            />
          </div>
        )}
      </div>
      <div className={classNames(styles.section)}>
        <div className={styles.sectionHeader}>
          <p className={styles.sectionTitle}>Računi</p>
        </div>
        <div>
          <ResourceList
            rowRender={renderResourceListRow}
            onSort={onSort}
            onLoadPage={onLoadPage}
            headerValues={HEADER_VALUES}
            resource={invoices}
          />
        </div>
      </div>
    </div>
  );
};

export default SingleVendingController;
